import { hydrateAuthState, login, externalLogin, register, logout } from "./authSlice";
import { createSlice, createAsyncThunk, isAnyOf } from "@reduxjs/toolkit";
import { getApiUrl, fetchWithAuth } from "../../services/apiService";

const CHAPTER_FEED_ENDPOINTS = {
    latestCompleted: "api/chapterfeed/completed",
    latestSubscriptions: "api/chapterfeed/subscriptions",
};

const CACHE_TTL = 10 * 60 * 1000;

const getErrorMessage = (t, key, fallback) => {
    return typeof t === "function" ? t(key) : fallback;
};

export const fetchLatestChapters = createAsyncThunk(
    "latestChapters/fetch",
    async (t, thunkAPI) => {
        try {
            const url = getApiUrl(CHAPTER_FEED_ENDPOINTS.latestCompleted);
            const res = await fetchWithAuth(url, { method: "GET" });

            return Array.isArray(res) ? res : [];
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err.message ||
                getErrorMessage(
                    t,
                    "latestChapters.errors.loadLatest",
                    "Ошибка загрузки глав"
                )
            );
        }
    },
    {
        condition: (_, { getState }) => {
            const state = getState().latestChapters;

            if (state.latestLoading) {
                return false;
            }

            const lastFetched = state.latestLastFetched || 0;
            const isFresh = Date.now() - lastFetched < CACHE_TTL;

            if (isFresh) {
                return false;
            }

            return true;
        },
    }
);

export const fetchSubscribedChapters = createAsyncThunk(
    "latestChapters/fetchSubscribed",
    async (t, thunkAPI) => {
        try {
            const url = getApiUrl(CHAPTER_FEED_ENDPOINTS.latestSubscriptions);
            const res = await fetchWithAuth(url, { method: "GET" });

            return Array.isArray(res) ? res : [];
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err.message ||
                getErrorMessage(
                    t,
                    "latestChapters.errors.loadSubscriptions",
                    "Ошибка загрузки подписанных глав"
                )
            );
        }
    },
    {
        condition: (_, { getState }) => {
            const { auth, latestChapters: state } = getState();
            if (!auth.isResolved || !auth.isLoggedIn) return false;

            if (state.subscribedLoading) {
                return false;
            }

            const lastFetched = state.subscribedLastFetched || 0;
            const isFresh = Date.now() - lastFetched < CACHE_TTL;

            if (isFresh) {
                return false;
            }

            return true;
        },
    }
);

const latestChaptersSlice = createSlice({
    name: "latestChapters",
    initialState: {
        latestChapters: [],
        subscribedChapters: [],

        latestLastFetched: null,
        subscribedLastFetched: null,

        latestLoading: false,
        subscribedLoading: false,
        subscribedRequestId: null,

        error: null,
    },
    reducers: {
        invalidateLatestChaptersCache: (state) => {
            state.latestLastFetched = null;
            state.subscribedLastFetched = null;
        },

        clearLatestChapters: (state) => {
            state.latestChapters = [];
            state.subscribedChapters = [];
            state.latestLastFetched = null;
            state.subscribedLastFetched = null;
            state.error = null;
            state.subscribedLoading = false;
            state.subscribedRequestId = null;
        },
    },
    extraReducers: (builder) => {
        builder
             .addCase(fetchLatestChapters.pending, (state) => {
                state.latestLoading = true;
                state.error = null;
            })
            .addCase(fetchLatestChapters.fulfilled, (state, action) => {
                state.latestChapters = action.payload;
                state.latestLastFetched = Date.now();
                state.latestLoading = false;
            })
            .addCase(fetchLatestChapters.rejected, (state, action) => {
                state.latestLoading = false;
                state.error = action.payload || "Произошла ошибка";
            })

            .addCase(fetchSubscribedChapters.pending, (state, action) => {
                state.subscribedRequestId = action.meta.requestId;
                state.subscribedLoading = true;
                state.error = null;
            })
            .addCase(fetchSubscribedChapters.fulfilled, (state, action) => {
                if (state.subscribedRequestId !== action.meta.requestId) return;
                state.subscribedRequestId = null;
                state.subscribedChapters = action.payload;
                state.subscribedLastFetched = Date.now();
                state.subscribedLoading = false;
            })
            .addCase(fetchSubscribedChapters.rejected, (state, action) => {
                if (state.subscribedRequestId !== action.meta.requestId) return;
                state.subscribedRequestId = null;
                state.subscribedLoading = false;
                state.error =
                    action.payload || "Ошибка загрузки подписанных глав";
            })
            .addMatcher(isAnyOf(hydrateAuthState, login.fulfilled, externalLogin.fulfilled,
                register.fulfilled, logout.fulfilled), (state) => {
                state.subscribedChapters = [];
                state.subscribedLastFetched = null;
                state.subscribedLoading = false;
                state.subscribedRequestId = null;
            });
    },
});

export const {
    invalidateLatestChaptersCache,
    clearLatestChapters,
} = latestChaptersSlice.actions;

export default latestChaptersSlice.reducer;